import { Injectable, Optional } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { HttpClient } from '@angular/common/http';
import { UserService } from './user.service';
import { Platform, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { LanguageService } from './language.service';
import { AnnouncementTypeType } from '../models/announcement';
import { DirectusTranslation } from '../models/directus-translation';
import { TranslationService } from './translation.service';

export interface DirectusPushNotificationRegister {
  id?: string;
  date_created?: string;
  date_updated?: string;
  tags?: string[];
  area?: string;
  token?: string;
}

export interface AzureHubSendNotificationPayload {
  tag: string;
  area_id: string;
  area_slug: string;
  count: number;
  content?: {
    translations: DirectusTranslation[];
    slug: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private pushServerUrl = environment.pushNotificationServerUrl;

  constructor(
    @Optional() private messagingInstance: Messaging,
    private http: HttpClient,
    private userService: UserService,
    private toastController: ToastController,
    public translate: TranslateService,
    private router: Router,
    private languageService: LanguageService,
    private translationService: TranslationService,
    private platform: Platform,
  ) {}

  initPushNotifications() {
    if (!('Notification' in window)) {
      console.warn('Browser does not support Notification');
      return;
    }

    const platforms = this.platform.platforms();
    const permission = Notification.permission;

    // Apple devices have specific criteria for notifications to work
    // i.e. permissions must be given by user interaction
    if (platforms.includes('ios') && permission === 'default') {
      return;
    }

    if (this.messagingInstance) {
      this.getDeviceToken();
      this.listenToForegroundMessages();
    }

    this.userService.pushNotificationChangedSubject.subscribe(() => {
      console.log('Push notification data has changed');
      this.sendPushDataToServer();
    });

    if (navigator && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.data) {
          if (event.data.action === 'BG_NOTIFICATION_CLICK') {
            this.openAnnouncement(event.data.data);
          }
        }
      });
    }
  }

  // Gets firebase token and asks for permissions if none have yet been given
  getDeviceToken() {
    getToken(this.messagingInstance)
      .then((firebaseToken) => {
        if (firebaseToken) {
          this.userService.setPushNotification({ token: firebaseToken });
          console.log('Push notification permission granted. Token:', firebaseToken);
        }
      })
      .catch((error) => {
        console.error('An error occured while retrieving firebase token', error);
      });
  }

  // Listens for notifications when app is in focus (in the foreground). Apple devices
  // will revoke token subscription after 3 notifications for failure to post to iOS
  // Notification Center: https://github.com/firebase/firebase-js-sdk/issues/6620
  listenToForegroundMessages() {
    onMessage(this.messagingInstance, (payload) => {
      const data: AzureHubSendNotificationPayload = payload.data as any;
      this.presentToast(data);
    });
  }

  parseNotificationContent(data: AzureHubSendNotificationPayload): {
    title: string;
    body?: string;
  } {
    const announcementType = data.tag;
    const hasManyAnnouncements = data.content ? false : true;
    const headerTranslation = `push.toast.header.${announcementType}.${
      hasManyAnnouncements ? 'group' : 'single'
    }`;
    const header: string = this.translate.instant(headerTranslation);
    const text: { title: string; body?: string } = {
      title: header,
    };
    if (data.content) {
      const content: { translations: DirectusTranslation[]; slug: string } = JSON.parse(
        data.content as any,
      );
      const translated: string = this.getTranslation(content).title;
      text.body = translated;
    }
    return text;
  }

  async presentToast(data: AzureHubSendNotificationPayload) {
    const parsed = this.parseNotificationContent(data);

    const toast = await this.toastController.create({
      duration: 10000,
      position: 'top',
      header: parsed.title,
      cssClass: 'push-notification-toast',
      animated: true,
      buttons: [
        {
          text: this.translate.instant('push.toast.button.open'),
          role: 'cancel',
          side: 'end',
          handler: () => {
            this.openAnnouncement(data);
          },
        },
      ],
    });

    if (data.content) {
      toast.message = parsed.body;
    }

    await toast.present();
  }

  openAnnouncement(data: AzureHubSendNotificationPayload) {
    let navigateTo: string = '';
    if (data.content) {
      navigateTo = JSON.parse(data.content as any).slug;
    }
    const organizationSlug = this.userService.user.organizationSlug;

    let tab = '';
    switch (data.tag) {
      case AnnouncementTypeType.ANNOUNCEMENT:
        tab = AnnouncementTypeType.ANNOUNCEMENT;
        break;
      case AnnouncementTypeType.NOTICE:
        tab = AnnouncementTypeType.NOTICE;
        break;
      case AnnouncementTypeType.WARNING:
        tab = AnnouncementTypeType.ANNOUNCEMENT;
        break;
      case AnnouncementTypeType.BLOG:
        tab = AnnouncementTypeType.BLOG;
        break;
      default:
        tab = AnnouncementTypeType.ANNOUNCEMENT;
        break;
    }

    let url = `${this.languageService.currentLanguage}/${organizationSlug}/announcement?tab=${tab}`;
    if (navigateTo) {
      url = `${this.languageService.currentLanguage}/${organizationSlug}/announcement/${navigateTo}?view=${tab}`;
    }
    this.router.navigateByUrl(url);
  }

  async sendPushDataToServer() {
    const tagsToSave: string[] = [];

    const areaId = this.userService.user.areaId;
    const organizationSlug = this.userService.user.organizationSlug;
    const token = this.userService.user.userdata.push_notification.token;
    const tags = this.userService.user.userdata.push_notification.tags;

    if (!token || !areaId) {
      console.warn('Cannot register push notification device. Missing token or area data.');
      return;
    }

    tagsToSave.push(...tags, organizationSlug);

    const payload: DirectusPushNotificationRegister = {
      token: token,
      tags: tagsToSave,
      area: areaId,
    };

    const response = await this.http
      .put(this.pushServerUrl, payload, { responseType: 'text' })
      .toPromise();
  }

  getTranslation(data: any) {
    return this.translationService.getCorrectTranslation(data);
  }

  async requestNotificationPermission() {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.initPushNotifications();
      }
      return permission;
    } catch (error) {
      console.error('Error requesting permission for push notifications:', error);
      return 'error';
    }
  }
}
